import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import './Dialog_ProviderChat.scss';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { asArray, exists, sortArray } from '@nu-art/ts-common';
import { ProviderChatInteractionStatus, ProviderChatStatus } from '@app/advisor-shared';
import { _className, Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Dialog, ModuleFE_MouseInteractivity, mouseInteractivity_ToolTip, openContent, stopPropagation, TS_CollapsableContainerV2 } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_EMInterface, ModuleFE_ProviderChat, ModuleFE_ProviderChatInteraction } from '@app/advisor-frontend';
import { TS_TextAreaV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_TextArea/TS_TextAreaV2';
import { iconTooltipId } from '../../_style/icon-tooltip/index.js';
const dialogKey = 'dialog__provider-chat';
const defaultActions = [
    { title: 'Admit Patient?', prompt: 'Would you recommend this patient be admitted?' },
    { title: 'Next Diagnosis Step?', prompt: 'What is the next diagnostic step based on these labs?' },
    { title: 'Differential Diagnosis?', prompt: 'What is the differential diagnosis for this patient’s chief complaint?' },
    { title: 'Crash Risk?', prompt: 'Is this patient at risk of crashing?' },
    { title: 'Safe for discharge?', prompt: 'Is this patient safe for discharge?' },
    { title: 'Summarize patient', prompt: 'Summarize this patient\'s ED course so far' },
    { title: 'Safe for IV Contrast?', prompt: 'Is this patient safe to receive IV contrast?' }
];
export class Dialog_ProviderChat extends ComponentSync {
    inputRef = React.createRef();
    //######################### Static #########################
    static show = () => {
        const model = {
            dialogKey: dialogKey,
            content: () => _jsx(Dialog_ProviderChat, {}),
            overlayClass: 'blur',
            closeOverlayOnClick: () => false
        };
        ModuleFE_Dialog.show(model);
    };
    //######################### Life Cycle #########################
    __onProviderChatsUpdated = (...params) => {
        if (!this.state.selectedChat)
            return this.setState({ chats: this.getSortedChats() });
        const updatedSelectedChat = ModuleFE_ProviderChat.cache.unique(this.state.selectedChat._id);
        this.setState({
            chats: this.getSortedChats(),
            selectedChat: updatedSelectedChat,
        });
    };
    __onProviderChatInteractionsUpdated = (...params) => {
        const currentChatId = this.state.selectedChat?._id;
        if (!currentChatId)
            return;
        const updatedInteractions = asArray(params[1]);
        const connectedChatIds = updatedInteractions.map(interaction => interaction.chatId);
        if (connectedChatIds.includes(currentChatId))
            this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        state.overlayOpened ??= false;
        state.actionInProgress ??= false;
        state.moreDefaults ??= false;
        state.chats = this.getSortedChats();
        return state;
    }
    //######################### Logic #########################
    getSortedChats() {
        return sortArray(ModuleFE_ProviderChat.cache.allMutable(), chat => chat.__updated, true);
    }
    closeDialog = () => {
        ModuleFE_Dialog.close(undefined, dialogKey);
    };
    setPrompt = (prompt) => {
        this.setState({ prompt }, () => this.inputRef.current?.focus());
    };
    sendPrompt = async () => {
        if (!prompt)
            return;
        return new Promise((resolve) => {
            ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip);
            const prompt = this.state.prompt;
            if (!prompt)
                return;
            this.setState({
                actionInProgress: true
            });
            ModuleFE_EMInterface.vv1.chatting.sendPrompt({
                chatId: this.state.selectedChat?._id,
                prompt: prompt.trim(),
            }).execute((response) => {
                this.setState({
                    selectedChat: response.chat,
                    actionInProgress: false,
                    prompt: undefined,
                }, () => resolve());
            }, (err) => {
                this.logError('Error while calling send prompt', err);
                this.setState({ actionInProgress: false }, () => resolve());
            });
        });
    };
    selectChat = (chat) => {
        this.setState({
            selectedChat: chat,
            overlayOpened: false
        }, () => this.inputRef.current?.focus());
    };
    //######################### Render #########################
    render() {
        return _jsxs("div", { id: dialogKey, children: [this.render_Stage(), this.render_Overlay()] });
    }
    //######################### Render - Stage #########################
    render_Stage = () => {
        return _jsxs(LL_V_L, { className: 'provider-chat__stage', onClick: () => this.setState({ overlayOpened: false }), children: [this.render_StageHeader(), this.render_Chat()] });
    };
    render_StageHeader = () => {
        return _jsxs(LL_H_C, { className: 'provider-chat__stage-header', children: [_jsx(ICONS.menu.component, { onClick: e => {
                        stopPropagation(e);
                        this.setState({ overlayOpened: true });
                    }, className: 'menu-icon' }), exists(this.state.selectedChat) && _jsxs(Button, { variant: 'secondary', onClick: () => this.selectChat(), children: [_jsx(ICONS.fillForm.component, {}), " New Chat"] }), _jsx(ICONS.advisor_avn_close.component, { onClick: this.closeDialog })] });
    };
    render_Chat = () => {
        if (!this.state.selectedChat)
            return this.render_EmptyChat();
        return this.render_SelectedChat(this.state.selectedChat);
    };
    render_PromptInput = () => {
        return _jsxs(LL_H_C, { className: 'provider-chat__prompt-input', children: [_jsx(TS_TextAreaV2, { innerRef: this.inputRef, value: this.state.prompt, onChange: value => this.setState({ prompt: value }), resizeWithText: true }), _jsx(Button, { onClick: this.sendPrompt, disabled: !this.state.prompt || this.state.actionInProgress, children: _jsx(ICONS.send.component, {}) })] });
    };
    //######################### Render - Empty Chat #########################
    render_EmptyChat = () => {
        return _jsxs(LL_V_L, { className: 'provider-chat__empty-chat', children: [this.render_DefaultPrompts(), this.render_PromptInput()] });
    };
    render_DefaultPrompts = () => {
        const actions = this.state.moreDefaults ? defaultActions : defaultActions.slice(0, 4);
        return _jsxs(LL_V_L, { className: 'provider-chat__empty-chat__defaults', children: [_jsx("h1", { children: "What can I help with?" }), _jsxs(LL_H_C, { children: [actions.map((action, index) => {
                            return _jsx(Button, { variant: 'secondary', onClick: () => this.setPrompt(action.prompt), ...openContent.tooltip.top(iconTooltipId, action.prompt, { offset: 8 }), children: action.title }, index);
                        }), this.render_DefaultPrompts_More()] })] });
    };
    render_DefaultPrompts_More = () => {
        return _jsx(Button, { variant: 'secondary', onClick: () => this.setState({ moreDefaults: !this.state.moreDefaults }), children: this.state.moreDefaults ? 'Less' : 'More' });
    };
    //######################### Render - Selected Chat #########################
    render_SelectedChat = (chat) => {
        const interactions = ModuleFE_ProviderChatInteraction.cache.filter(interaction => interaction.chatId === chat._id);
        return _jsxs(LL_V_L, { className: 'provider-chat__selected-chat', onClick: e => {
                if (e.metaKey) {
                    this.logInfo('Chat', chat);
                    this.logInfo('Interactions', interactions);
                }
            }, children: [_jsxs(FadeScrollWrapper, { children: [interactions.map(this.render_ChatInteraction), this.render_InteractionPending(chat)] }), this.render_PromptInput()] });
    };
    render_ChatInteraction = (interaction) => {
        return _jsxs(React.Fragment, { children: [this.render_InteractionPrompt(interaction), this.render_InteractionError(interaction), interaction.answers.map((answer, index) => this.render_InteractionResponse(answer, index))] }, interaction._id);
    };
    render_InteractionPrompt = (interaction) => {
        return _jsx("div", { className: 'provider-chat__interaction-prompt', children: interaction.prompt });
    };
    render_InteractionError = (interaction) => {
        if (interaction.status !== ProviderChatInteractionStatus.Failed)
            return;
        return _jsx(TS_CollapsableContainerV2, { className: 'provider-chat__interaction-error', customCaret: () => _jsx(ICONS.advisor_v5_warning_exclamation_mark.component, {}), headerRenderer: 'An error occurred. If this issue persists please contact us!', containerRenderer: interaction.errorMessage ?? 'No error message provided', animated: true });
    };
    render_InteractionResponse = (answer, responseIndex) => {
        return _jsx("div", { className: 'provider-chat__interaction-answer', children: answer.answer }, responseIndex);
    };
    render_InteractionPending = (chat) => {
        if (chat.status !== ProviderChatStatus.Processing)
            return;
        return _jsx("div", { className: 'provider-chat__interaction-pending' });
    };
    //######################### Render - Overlay #########################
    render_Overlay = () => {
        const className = _className('provider-chat__overlay', this.state.overlayOpened && 'opened');
        return _jsxs(LL_V_L, { className: className, children: [this.render_OverlayHeader(), this.render_ChatHistory()] });
    };
    render_OverlayHeader = () => {
        return _jsx(LL_H_C, { className: 'provider-chat__overlay-header', children: exists(this.state.selectedChat) && _jsxs(Button, { variant: 'secondary', onClick: () => this.selectChat(), children: [_jsx(ICONS.fillForm.component, {}), " New Chat"] }) });
    };
    render_ChatHistory = () => {
        return _jsx(FadeScrollWrapper, { className: 'provider-chat__chat-history', children: this.state.chats.map(chat => {
                return _jsx("div", { onClick: () => this.selectChat(chat), children: chat.title }, chat._id);
            }) });
    };
}
//# sourceMappingURL=Dialog_ProviderChat.js.map